import React, { memo, useCallback, useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { rem } from 'design-system/values';
import SearchTransactionUpgradeBanner, { SEARCH_UPGRADE_BANNER_HEIGHT, } from 'features/search/components/SearchTransactionUpgradeBanner';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useStyles from 'hooks/useStyles';
import KeyboardAccounts from './KeyboardAccounts';
import KeyboardCategories from './KeyboardCategories';
import KeyboardDates from './KeyboardDates';
import KeyboardMerchants from './KeyboardMerchants';
import KeyboardTags from './KeyboardTags';
import { keyboardToFilterMap } from './colors';
import { TOOLBAR_BUTTONS, TOOLBAR_PADDING_VERTICAL, calculateSize } from './config';
import styleSet from './styles';
import ToolbarButton from './icons/ToolbarButton';
import { isDesktopOrWeb, isWeb } from '../../../../constants';
import { useKeyboardHeightWeb } from './hooks/useKeyboardHeightWeb';
import KeyboardSettings from './KeyboardSettings';
const HIT_SLOP = { top: 10, bottom: 10, left: rem(4), right: rem(4) };
const SearchToolbar = ({ onSelectAll, refAmountLowerInput, refAmountUpperInput, activeFilter, onSelectFilter, isUnlocked, onPressUpgrade, keyboardHeight, activeKeyboard, settingsFilter, onRemoveFilter, showSpecialKeyboard, onChangeSettingsFilter, }) => {
    const webKeyboardHeight = useKeyboardHeightWeb();
    const { paddingHorizontalStyle, width: frameWidth } = useAppFrameDimensions(isWeb);
    const onClickFilter = useCallback((_keyboardId, params) => {
        onSelectFilter(params);
    }, [onSelectFilter]);
    const styles = useStyles(styleSet);
    const toolbarButtons = useMemo(() => {
        const size = isWeb ? 36 : calculateSize(frameWidth);
        return (<View style={[styles.toolbarContainer, { width: frameWidth }]}>
        {TOOLBAR_BUTTONS.map((button) => {
                const active = button.keyboardName === activeKeyboard;
                const filterType = keyboardToFilterMap[button.keyboardName];
                // eslint-disable-next-line no-nested-ternary
                const hasFilter = filterType
                    ? filterType === 'SETTINGS'
                        ? settingsFilter !== 'DEFAULT'
                        : !!activeFilter[filterType]
                    : false;
                return (<ToolbarButton key={button.keyboardName} hasFilter={hasFilter} size={size} active={active} hitSlop={HIT_SLOP} icon={<button.Icon />} onPress={() => {
                        showSpecialKeyboard(button.keyboardName);
                        if (button.keyboardName === 'KeyboardAmountLower') {
                            refAmountLowerInput.current?.focus();
                        }
                        if (button.keyboardName === 'KeyboardAmountUpper') {
                            refAmountUpperInput.current?.focus();
                        }
                    }}/>);
            })}
      </View>);
    }, [
        frameWidth,
        styles.toolbarContainer,
        activeKeyboard,
        settingsFilter,
        activeFilter,
        showSpecialKeyboard,
        refAmountLowerInput,
        refAmountUpperInput,
    ]);
    const specialKeyboard = useMemo(() => {
        if (!activeKeyboard) {
            return null;
        }
        switch (activeKeyboard) {
            case 'KeyboardCategories':
                return (<KeyboardCategories onSelect={onClickFilter} onSelectAll={onSelectAll} onRemoveFilter={onRemoveFilter} settingsFilter={settingsFilter} keyboardHeight={keyboardHeight} activeFilters={activeFilter.CATEGORY} contentContainerStyle={paddingHorizontalStyle}/>);
            case 'KeyboardTags':
                return (<KeyboardTags onSelect={onClickFilter} settingsFilter={settingsFilter} keyboardHeight={keyboardHeight} activeFilters={activeFilter.TAG} contentContainerStyle={paddingHorizontalStyle}/>);
            case 'KeyboardMerchants':
                return (<KeyboardMerchants onSelect={onClickFilter} settingsFilter={settingsFilter} keyboardHeight={keyboardHeight} activeFilters={activeFilter.MERCHANT} contentContainerStyle={paddingHorizontalStyle}/>);
            case 'KeyboardDates': {
                let dateFilter = null;
                if (activeFilter && Object.keys(activeFilter).length) {
                    dateFilter = activeFilter.DATES;
                }
                return (<KeyboardDates activeDateFilter={dateFilter} onClickFilter={onClickFilter} onRemoveFilter={onRemoveFilter} contentContainerStyle={paddingHorizontalStyle}/>);
            }
            case 'KeyboardAccounts':
                return (<KeyboardAccounts onSelect={onClickFilter} onSelectAll={onSelectAll} onRemoveFilter={onRemoveFilter} settingsFilter={settingsFilter} keyboardHeight={keyboardHeight} activeFilters={activeFilter.ACCOUNT} contentContainerStyle={paddingHorizontalStyle}/>);
            case 'KeyboardSettings':
                return (<KeyboardSettings settingsFilter={settingsFilter} onChangeSettingsFilter={onChangeSettingsFilter} contentContainerStyle={paddingHorizontalStyle}/>);
            default:
                break;
        }
        return null;
    }, [
        activeKeyboard,
        onClickFilter,
        onSelectAll,
        onRemoveFilter,
        settingsFilter,
        keyboardHeight,
        activeFilter,
        paddingHorizontalStyle,
        onChangeSettingsFilter,
    ]);
    const specialKeyboardWeb = useMemo(() => {
        if (!activeKeyboard) {
            return null;
        }
        switch (activeKeyboard) {
            case 'KeyboardCategories':
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardCategories onSelect={onClickFilter} onSelectAll={onSelectAll} onRemoveFilter={onRemoveFilter} keyboardHeight={keyboardHeight} settingsFilter={settingsFilter} activeFilters={activeFilter.CATEGORY}/>
          </View>);
            case 'KeyboardTags':
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardTags onSelect={onClickFilter} settingsFilter={settingsFilter} keyboardHeight={keyboardHeight} activeFilters={activeFilter.TAG}/>
          </View>);
            case 'KeyboardMerchants':
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardMerchants onSelect={onClickFilter} keyboardHeight={keyboardHeight} settingsFilter={settingsFilter} activeFilters={activeFilter.MERCHANT}/>
          </View>);
            case 'KeyboardDates': {
                let dateFilter = null;
                if (activeFilter && Object.keys(activeFilter).length) {
                    dateFilter = activeFilter.DATES;
                }
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardDates activeDateFilter={dateFilter} onClickFilter={onClickFilter} onRemoveFilter={onRemoveFilter}/>
          </View>);
            }
            case 'KeyboardAccounts':
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardAccounts onSelect={onClickFilter} onSelectAll={onSelectAll} onRemoveFilter={onRemoveFilter} keyboardHeight={keyboardHeight} settingsFilter={settingsFilter} activeFilters={activeFilter.ACCOUNT}/>
          </View>);
            case 'KeyboardSettings':
                return (<View style={[styles.keyboardWrapper, paddingHorizontalStyle]}>
            <KeyboardSettings settingsFilter={settingsFilter} onChangeSettingsFilter={onChangeSettingsFilter}/>
          </View>);
            default:
                break;
        }
        return null;
    }, [
        activeKeyboard,
        styles.keyboardWrapper,
        paddingHorizontalStyle,
        onClickFilter,
        onSelectAll,
        onRemoveFilter,
        keyboardHeight,
        settingsFilter,
        activeFilter,
        onChangeSettingsFilter,
    ]);
    const size = calculateSize(frameWidth);
    const showWithoutKeyboard = Platform.OS === 'ios' && Platform.isPad && specialKeyboard;
    if (!isDesktopOrWeb && !keyboardHeight && !showWithoutKeyboard) {
        if (!isUnlocked) {
            return (<View style={styles.toolbarWrap}>
          <SearchTransactionUpgradeBanner isKeyboardVisible={false} onPress={onPressUpgrade}/>
        </View>);
        }
        return null;
    }
    const toolbarHeight = (() => {
        if (isDesktopOrWeb) {
            if (activeKeyboard === 'KeyboardCategories' ||
                activeKeyboard === 'KeyboardMerchants' ||
                activeKeyboard === 'KeyboardTags' ||
                activeKeyboard === 'KeyboardDates' ||
                activeKeyboard === 'KeyboardAccounts' ||
                activeKeyboard === 'KeyboardSettings') {
                return webKeyboardHeight;
            }
            return size + 2 * TOOLBAR_PADDING_VERTICAL + (isUnlocked ? 0 : SEARCH_UPGRADE_BANNER_HEIGHT);
        }
        if (keyboardHeight < 100 && showWithoutKeyboard) {
            return 300;
        }
        return keyboardHeight + (isUnlocked ? 0 : SEARCH_UPGRADE_BANNER_HEIGHT);
    })();
    return (<View style={[
            styles.toolbarWrap,
            {
                height: toolbarHeight,
            },
        ]}>
      {!isWeb && !isUnlocked && <SearchTransactionUpgradeBanner isKeyboardVisible onPress={onPressUpgrade}/>}
      <View style={styles.toolbarWrapInner}>
        <ScrollView style={{ height: size + 2 * TOOLBAR_PADDING_VERTICAL }} contentContainerStyle={[{ height: size + 2 * TOOLBAR_PADDING_VERTICAL }, paddingHorizontalStyle]} showsHorizontalScrollIndicator={false} scrollEnabled={false} keyboardShouldPersistTaps="always" horizontal>
          {toolbarButtons}
        </ScrollView>
      </View>
      {isDesktopOrWeb ? specialKeyboardWeb : specialKeyboard}
    </View>);
};
export default memo(SearchToolbar);
